import { useAuth } from '@verantos-dev/core-auth';

import { CurationPlatformAppId, getEntitledCurationPlatformApps } from './curationPlatformApps';
import { getCurationPlatformAppUrl } from './curationPlatformUrls';

export interface UseCurationPlatformFallbackAppUrlOptions {
  /**
   * Which Curation Platform app this hook is called from — a build-time
   * fact passed in by the caller, never derived from the URL.
   */
  currentAppId: CurationPlatformAppId;
  /**
   * cp_no_access_redirect_fix flag value. Returns null when false.
   */
  // TODO: cleanup with cp_no_access_redirect_fix ff
  isEnabled: boolean;
}

/**
 * Resolves the absolute URL of the Curation Platform app to send the user to
 * when they hold no role for the current app but do hold one for another app
 * (an Accuracy Viewer on the Annotation Tool, or an Annotation User/Manager on
 * the Accuracy Dashboard). Returns null when the user is entitled to the
 * current app, is entitled to no other app, or that app's URL is not
 * configured (getCurationPlatformAppUrl reports the misconfiguration to
 * Sentry).
 */
export const useCurationPlatformFallbackAppUrl = ({
  currentAppId,
  isEnabled,
}: UseCurationPlatformFallbackAppUrlOptions): string | null => {
  const { user } = useAuth();

  // TODO: cleanup with cp_no_access_redirect_fix ff
  if (!isEnabled) {
    return null;
  }

  const entitledApps = getEntitledCurationPlatformApps(user.roles.map((role) => role.roleId));

  // A user entitled to the current app stays on it: any no-access state they
  // hit there comes from a route-level role check (e.g. an Annotation User
  // opening an Annotation Manager page), which leaving the app would not
  // resolve.
  if (entitledApps.some((app) => app.id === currentAppId)) {
    return null;
  }

  const fallbackApp = entitledApps[0];
  return fallbackApp ? getCurationPlatformAppUrl(fallbackApp.id) : null;
};
